<template>
  <div>
    <d-form>
      <d-form-row class="form-group">
        <d-col cols="12" md="8">
          <label>{{ $t('numerex.form.name.label') }}</label>
          <d-form-input
            ref="nameInput"
            type="text"
            v-model="model.name"
            :state="hasErrorFiled('name')"
            :placeholder="$t('numerex.form.name.placeholder')"
          />
          <d-form-invalid-feedback>
            {{ $t('numerex.form.name.invalid') }}
          </d-form-invalid-feedback>
        </d-col>
        <d-col cols="12" md="4">
          <label>
            {{ $t('numerex.form.level.label') }}
          </label>
          <custom-select
            v-model="levelOption"
            :options="levelOptions"
            trackBy="slug"
            :placeholder="$t('numerex.form.level.placeholder')"
            @change="setLevel"
          >
            <template #option="{ option }">
              <i :class="option.color" class="fas fa-circle mr-2" />
              {{ $t(option.labelKey) }}
            </template>
            <template #selected="{ option }">
              {{ $t(option.labelKey) }}
            </template>
          </custom-select>
        </d-col>
      </d-form-row>

      <d-form-row class="form-group align-items-end">
        <d-col cols="6" md="2">
          <label>{{ $t('numerex.form.rows.label') }}</label>
          <d-form-input
            type="number"
            :min="minDimensions.ROWS"
            :max="maxDimensions.ROWS"
            v-model.number="draftRows"
            @change="onDimensionsChange"
          />
        </d-col>
        <d-col cols="6" md="2">
          <label>{{ $t('numerex.form.columns.label') }}</label>
          <d-form-input
            type="number"
            :min="minDimensions.COLUMNS"
            :max="maxDimensions.COLUMNS"
            v-model.number="draftColumns"
            @change="onDimensionsChange"
          />
        </d-col>
        <d-col cols="12" md="4">
          <label>{{ $t('numerex.form.generationMode.label') }}</label>
          <custom-select
            v-model="generationModeOption"
            :options="generationModeOptions"
            trackBy="slug"
            :placeholder="$t('numerex.form.generationMode.placeholder')"
            @change="setGenerationMode"
          >
            <template #option="{ option }">
              {{ $t(option.labelKey) }}
            </template>
            <template #selected="{ option }">
              {{ $t(option.labelKey) }}
            </template>
          </custom-select>
        </d-col>
        <d-col cols="12" md="4" class="d-flex flex-column justify-content-end">
          <d-checkbox
            toggle
            v-model="model.settings.prioritize_quality"
            class="custom-toggle-sm"
            @change="markChanged"
          >
            <span class="mr-1">
              {{ $t('numerex.form.prioritizeQuality.label') }}
            </span>
            <i
              id="tooltip-numerex-prioritize_quality"
              class="fas fa-info-circle text-muted"
              style="cursor: pointer"
            />
            <d-tooltip
              target="tooltip-numerex-prioritize_quality"
              placement="top"
            >
              {{ $t('numerex.form.prioritizeQuality.tooltip') }}
            </d-tooltip>
          </d-checkbox>
        </d-col>
      </d-form-row>

      <div v-if="showsNumberLengthRange" class="form-group">
        <div class="d-flex align-items-center mb-3">
          <span class="text-nowrap mr-2 font-weight-bold">
            {{ $t('numerex.form.numberLengthRange.section') }}
          </span>
          <div class="flex-grow-1 border-top" />
        </div>

        <d-form-row>
          <d-col cols="12" md="6">
            <label>
              {{ $t('numerex.form.numberLengthRange.min.label') }}
              <i
                id="tooltip-numerex-min-number-length"
                class="fas fa-info-circle text-muted ml-1"
                style="cursor: pointer"
              />
              <d-tooltip
                target="tooltip-numerex-min-number-length"
                placement="top"
              >
                {{ $t('numerex.form.numberLengthRange.min.tooltip') }}
              </d-tooltip>
            </label>
            <d-form-input
              type="number"
              :min="numberLengthBounds.MIN"
              :max="maxNumberLengthBound"
              v-model.number="model.settings.min_number_length"
              @change="onNumberLengthRangeChange"
            />
          </d-col>
          <d-col cols="12" md="6">
            <label>
              {{ $t('numerex.form.numberLengthRange.max.label') }}
              <i
                id="tooltip-numerex-max-number-length"
                class="fas fa-info-circle text-muted ml-1"
                style="cursor: pointer"
              />
              <d-tooltip
                target="tooltip-numerex-max-number-length"
                placement="top"
              >
                {{ $t('numerex.form.numberLengthRange.max.tooltip') }}
              </d-tooltip>
            </label>
            <d-form-input
              type="number"
              :min="numberLengthBounds.MIN"
              :max="maxNumberLengthBound"
              v-model.number="model.settings.max_number_length"
              @change="onNumberLengthRangeChange"
            />
          </d-col>
        </d-form-row>
      </div>
    </d-form>

    <div v-if="numbersList" class="form-group mt-3">
      <label>
        {{ $t('numerex.form.numbersList.label') }}
        <i
          id="tooltip-numerex-copy-numbers-list"
          class="fas fa-copy text-muted ml-1"
          style="cursor: pointer"
          @click="copyNumbersList"
        />
        <d-tooltip target="tooltip-numerex-copy-numbers-list" placement="top">
          {{ $t('numerex.form.numbersList.copyTooltip') }}
        </d-tooltip>
      </label>
      <textarea
        class="form-control numbers-list"
        :value="numbersList"
        rows="8"
        readonly
      />
    </div>

    <div class="mt-3">
      <numerex-grid
        v-if="model.table.length"
        :table="model.table"
        :rows="model.rows"
        :columns="model.columns"
        :cell-size="cellSize"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import mixin from '_common/mixins/ValidateForm';
import FormatErrors from '_common/mixins/FormatErrors';
import CustomSelect from '_shared/CustomSelect.vue';
import PuzzleService from '_services/rest/modules/puzzle';
import Events from '_utils/events';
import { cloneDeep, getLevelOption, LEVEL_OPTIONS, LEVEL } from '_utils';
import NumerexGrid from './NumerexGrid.vue';
import {
  _LOAD_NUMEREX_MODEL_,
  buildEmptyTable,
  CELL_SIZE,
  clampDimension,
  DEFAULT_SETTINGS,
  GENERATION_MODE_OPTIONS,
  GENERATION_MODES,
  getGenerationModeOption,
  MAX_DIMENSIONS,
  MIN_DIMENSIONS,
  MODEL,
  normalizeNumberLengths,
  NUMBER_LENGTH_BOUNDS,
  TYPE_SLUG,
} from './NumerexModel';

export default {
  name: 'numerex-form',
  components: {
    CustomSelect,
    NumerexGrid,
  },
  mixins: [mixin, FormatErrors],
  data() {
    return {
      model: cloneDeep(MODEL),
      draftRows: MODEL.rows,
      draftColumns: MODEL.columns,
      errors: { name: { required: false } },
      cellSize: CELL_SIZE,
      minDimensions: MIN_DIMENSIONS,
      maxDimensions: MAX_DIMENSIONS,
      numberLengthBounds: NUMBER_LENGTH_BOUNDS,
      levelOptions: LEVEL_OPTIONS,
      levelOption: getLevelOption(LEVEL.MEDIUM),
      generationModeOptions: GENERATION_MODE_OPTIONS,
      generationModeOption: getGenerationModeOption(GENERATION_MODES.SIMPLE),
    };
  },
  computed: {
    generationMode() {
      return this.model.settings.generation_mode || GENERATION_MODES.SIMPLE;
    },
    showsNumberLengthRange() {
      return this.generationMode === GENERATION_MODES.DISTRIBUTION;
    },
    maxNumberLengthBound() {
      return Math.max(
        Number(this.model.rows) || 0,
        Number(this.model.columns) || 0,
        NUMBER_LENGTH_BOUNDS.MIN,
      );
    },
    numbersList() {
      return this.model.settings.numbers_list || '';
    },
  },
  methods: {
    ...mapActions(['activeAlert', 'setHasChanges', 'setNumerex']),
    markChanged() {
      this.setHasChanges(true);
    },
    syncDimensionDraft() {
      this.draftRows = this.model.rows;
      this.draftColumns = this.model.columns;
    },
    normalizeSettings(
      settings = {},
      rows = this.model.rows,
      columns = this.model.columns,
    ) {
      const mode = GENERATION_MODE_OPTIONS.some(
        option => option.slug === settings.generation_mode,
      )
        ? settings.generation_mode
        : GENERATION_MODES.SIMPLE;

      const maxBound = Math.max(
        Number(rows) || 0,
        Number(columns) || 0,
        NUMBER_LENGTH_BOUNDS.MIN,
      );

      let min = clampDimension(
        settings.min_number_length,
        NUMBER_LENGTH_BOUNDS.MIN,
        maxBound,
        NUMBER_LENGTH_BOUNDS.DEFAULT_MIN,
      );
      let max = clampDimension(
        settings.max_number_length,
        NUMBER_LENGTH_BOUNDS.MIN,
        maxBound,
        Math.min(NUMBER_LENGTH_BOUNDS.DEFAULT_MAX, maxBound),
      );

      if (min > max) {
        [min, max] = [max, min];
      }

      return {
        ...DEFAULT_SETTINGS,
        ...settings,
        generation_mode: mode,
        min_number_length: min,
        max_number_length: max,
        prioritize_quality: settings.prioritize_quality !== false,
        number_lengths: normalizeNumberLengths(settings.number_lengths),
        numbers_list:
          typeof settings.numbers_list === 'string'
            ? settings.numbers_list
            : '',
      };
    },
    applyModel(source) {
      const cloned = cloneDeep(source || {});
      const next = {
        ...cloneDeep(MODEL),
        ...cloned,
      };

      next.rows = clampDimension(
        next.rows,
        MIN_DIMENSIONS.ROWS,
        MAX_DIMENSIONS.ROWS,
        MODEL.rows,
      );
      next.columns = clampDimension(
        next.columns,
        MIN_DIMENSIONS.COLUMNS,
        MAX_DIMENSIONS.COLUMNS,
        MODEL.columns,
      );
      next.settings = this.normalizeSettings(
        cloned.settings || {},
        next.rows,
        next.columns,
      );

      if (!Array.isArray(next.table) || !next.table.length) {
        next.table = buildEmptyTable(next.rows, next.columns);
      } else {
        next.table = next.table.map(cell => ({
          id: cell.id || `cell-${cell.positionx}-${cell.positiony}`,
          positionx: cell.positionx,
          positiony: cell.positiony,
          text:
            cell.text != null && cell.text !== '' ? String(cell.text) : null,
        }));
      }

      this.model = next;
      this.syncDimensionDraft();
      this.levelOption = getLevelOption(next.level || LEVEL.MEDIUM);
      this.generationModeOption = getGenerationModeOption(
        next.settings.generation_mode,
      );
    },
    applyGenerateResponse(response = {}) {
      let payload = response;
      if (
        !Array.isArray(response.table) &&
        response.data &&
        Array.isArray(response.data.table)
      ) {
        payload = response.data;
      }

      if (Array.isArray(payload.table)) {
        this.model.table = cloneDeep(payload.table).map(cell => ({
          id: cell.id || `cell-${cell.positionx}-${cell.positiony}`,
          positionx: cell.positionx,
          positiony: cell.positiony,
          text:
            cell.text != null && cell.text !== '' ? String(cell.text) : null,
        }));
      }

      if (payload.rows) {
        this.model.rows = clampDimension(
          payload.rows,
          MIN_DIMENSIONS.ROWS,
          MAX_DIMENSIONS.ROWS,
          this.model.rows,
        );
      }

      if (payload.columns) {
        this.model.columns = clampDimension(
          payload.columns,
          MIN_DIMENSIONS.COLUMNS,
          MAX_DIMENSIONS.COLUMNS,
          this.model.columns,
        );
      }

      if (payload.settings) {
        this.model.settings = this.normalizeSettings({
          ...this.model.settings,
          ...payload.settings,
        });
        this.generationModeOption = getGenerationModeOption(
          this.model.settings.generation_mode,
        );
      }

      if (payload.level) {
        this.model.level = payload.level;
        this.levelOption = getLevelOption(payload.level);
      }

      this.syncDimensionDraft();
    },
    updateTable(markChanged = true) {
      const rows = clampDimension(
        this.model.rows,
        MIN_DIMENSIONS.ROWS,
        MAX_DIMENSIONS.ROWS,
        MODEL.rows,
      );
      const columns = clampDimension(
        this.model.columns,
        MIN_DIMENSIONS.COLUMNS,
        MAX_DIMENSIONS.COLUMNS,
        MODEL.columns,
      );

      this.model.rows = rows;
      this.model.columns = columns;
      this.model.table = buildEmptyTable(rows, columns, this.model.table);
      this.syncDimensionDraft();

      const settings = this.normalizeSettings(
        this.model.settings,
        rows,
        columns,
      );
      this.model.settings.min_number_length = settings.min_number_length;
      this.model.settings.max_number_length = settings.max_number_length;

      if (markChanged) {
        this.markChanged();
      }
    },
    onDimensionsChange() {
      this.model.rows = this.draftRows;
      this.model.columns = this.draftColumns;
      this.updateTable(true);
    },
    onNumberLengthRangeChange() {
      const settings = this.normalizeSettings(this.model.settings);
      this.model.settings.min_number_length = settings.min_number_length;
      this.model.settings.max_number_length = settings.max_number_length;
      this.markChanged();
    },
    setLevel(option) {
      this.model.level = option.slug;
      this.markChanged();
    },
    setGenerationMode(option) {
      this.model.settings.generation_mode = option.slug;
      this.generationModeOption = option;
      this.markChanged();
    },
    isNumberLengthRangeValid() {
      const { min_number_length: min, max_number_length: max } =
        this.model.settings;
      return (
        Number(min) >= NUMBER_LENGTH_BOUNDS.MIN &&
        Number(max) >= Number(min) &&
        Number(max) <= this.maxNumberLengthBound
      );
    },
    buildGeneratePayload() {
      const settings = this.normalizeSettings(this.model.settings);
      const payload = {
        rows: this.model.rows,
        columns: this.model.columns,
        level: this.model.level,
        options: {
          prioritize_quality: Boolean(settings.prioritize_quality),
        },
      };

      if (this.showsNumberLengthRange) {
        payload.min_number_length = settings.min_number_length;
        payload.max_number_length = settings.max_number_length;
      }

      return payload;
    },
    preparePayload() {
      const settings = this.normalizeSettings(this.model.settings);

      return {
        ...this.model,
        slug: `${this.model.name.replace(/\s+/g, '-')}-${TYPE_SLUG}`,
        settings: {
          number_lengths: settings.number_lengths,
          numbers_list: settings.numbers_list,
        },
        table: this.model.table.map(cell => ({
          id: cell.id || `cell-${cell.positionx}-${cell.positiony}`,
          positionx: cell.positionx,
          positiony: cell.positiony,
          text:
            cell.text != null && cell.text !== '' ? String(cell.text) : null,
        })),
      };
    },
    clear() {
      this.model.table = buildEmptyTable(this.model.rows, this.model.columns);
      this.model.settings = this.normalizeSettings({
        generation_mode: this.model.settings.generation_mode,
        prioritize_quality: this.model.settings.prioritize_quality,
        min_number_length: this.model.settings.min_number_length,
        max_number_length: this.model.settings.max_number_length,
      });
      this.markChanged();
      this.activeAlert({
        message: this.$t('numerex.alert.cleared'),
        theme: 'success',
      });
    },
    copyNumbersList() {
      if (!this.numbersList) return;

      navigator.clipboard
        .writeText(this.numbersList)
        .then(() => {
          this.activeAlert({
            message: this.$t('numerex.alert.numbersListCopied'),
            theme: 'success',
          });
        })
        .catch(() => {
          this.activeAlert({
            message: this.$t('numerex.alert.numbersListCopyFailed'),
            theme: 'danger',
          });
        });
    },
    generate() {
      if (this.showsNumberLengthRange && !this.isNumberLengthRangeValid()) {
        this.activeAlert({
          message: this.$t('numerex.alert.numberLengthRangeInvalid'),
          theme: 'warning',
        });
        return;
      }

      PuzzleService.generatePuzzle(TYPE_SLUG, this.buildGeneratePayload())
        .then(response => {
          this.applyGenerateResponse(response);
          this.markChanged();
          this.activeAlert({
            message: this.$t('numerex.alert.generated'),
            theme: 'success',
          });
        })
        .catch(error => {
          const errors = error.data ? this.formatErrors(error.data) : null;
          this.activeAlert({
            message:
              errors ||
              error.message ||
              this.$t('numerex.alert.generateFailed'),
            theme: 'danger',
          });
        });
    },
    save() {
      if (!this.checkForm()) return;

      if (this.showsNumberLengthRange && !this.isNumberLengthRangeValid()) {
        this.activeAlert({
          message: this.$t('numerex.alert.numberLengthRangeInvalid'),
          theme: 'warning',
        });
        return;
      }

      const puzzle = this.preparePayload();

      PuzzleService.save(puzzle)
        .then(response => {
          this.activeAlert({
            message: this.$t('numerex.alert.gameSaved'),
            theme: 'success',
          });
          this.setNumerex(response);
          if (this.$route.name === 'admin-numerex-new') {
            this.$router.push({
              name: 'admin-numerex-edit',
              params: { puzzleId: response.id },
            });
          }
          this.setHasChanges(false);
        })
        .catch(error => {
          const errors = this.formatErrors(error.data);
          this.activeAlert({ message: errors, theme: 'danger' });
        });
    },
  },
  mounted() {
    if (!this.model.table.length) {
      this.updateTable(false);
    }

    Events.$on(_LOAD_NUMEREX_MODEL_, model => {
      this.applyModel(model);
    });
  },
  beforeDestroy() {
    Events.$off(_LOAD_NUMEREX_MODEL_);
  },
};
</script>

<style scoped>
.numbers-list {
  min-height: 160px;
  resize: vertical;
  background-color: #f8f9fa;
  cursor: default;
}
</style>
